import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Action, Panel, PanelHead } from '../aa';
import { MyPart } from '../../pages/orbit-room/MyPart';
import '../../pages/orbit-room/room.css';
import useOpenWorkspace from '../../hooks/useOpenWorkspace';
import { setupApi } from '../../services/setupApi';
import type { SetupIdea } from '../../services/setupApi';
import { Chip, FIT_LABEL, KIND_LABEL } from '../../pages/setup/setupKit';

/*
 * The two things the setup leaves on every role's home: the ideas the person
 * picked, each one a click from the Workspace, and the questions that wait for
 * them after the setup, answered whenever they like.
 */

export const YourIdeasPanel: React.FC<{ className?: string }> = ({ className = 'mt-5' }) => {
  const [ideas, setIdeas] = useState<SetupIdea[]>([]);
  const { opening, open } = useOpenWorkspace();
  const [startingId, setStartingId] = useState('');
  const [searchParams] = useSearchParams();
  const companionTarget = searchParams.get('companion');

  useEffect(() => {
    setupApi.myIdeas()
      .then(({ data }) => {
        const picked = new Set(data.picked);
        const chosen = data.ideas.filter((i) => picked.has(i.id));
        setIdeas(chosen.length ? chosen : data.ideas);
      })
      .catch(() => setIdeas([]));
  }, []);

  useEffect(() => {
    if (ideas.length === 0 || companionTarget !== 'ideas') return;
    const timer = window.setTimeout(() => {
      document.getElementById('your-ideas')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [ideas.length, companionTarget]);

  if (ideas.length === 0) return null;
  const start = async (id: string) => {
    setStartingId(id);
    try {
      await open(localStorage.getItem('client_id'), async () => (await setupApi.handoffIdeas([id])).data.path);
    } finally {
      setStartingId('');
    }
  };

  return (
    <Panel className={`scroll-mt-20 ${className}`} id="your-ideas">
      <PanelHead kicker="From your setup" title="Your ideas" desc="Where AI can help you, from your role, your apps and your permissions." />
      <div className="px-5 pb-4 md:px-6">
        {ideas.map((idea) => {
          const [fitLabel, fitTone] = FIT_LABEL[idea.fit];
          let startLabel = 'Start in Workspace';
          if (startingId === idea.id) startLabel = 'Opening…';
          return (
            <div key={idea.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-line py-3 first:border-t-0">
              <div className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-1.5">
                  <b className="text-[14px] text-ink">{idea.title}</b>
                  <Chip>{KIND_LABEL[idea.kind]}</Chip>
                  <Chip tone={fitTone}>{fitLabel}</Chip>
                </span>
                <p className="m-0 mt-0.5 text-[12.5px] leading-[19px] text-ink-muted">{idea.summary}</p>
              </div>
              <Action size="sm" disabled={opening} reason="Opening your Workspace" onClick={() => start(idea.id)}>
                {startLabel}
              </Action>
            </div>
          );
        })}
        {startingId && (
          <p role="status" className="m-0 border-t border-line pt-3 text-[12.5px] leading-[19px] text-ink-muted">
            Your Workspace is opening in a new tab, and the chat starts there by itself. The first time can take up to a minute.
          </p>
        )}
      </div>
    </Panel>
  );
};

export const QuestionsForYouPanel: React.FC<{ className?: string; desc?: string }> = ({
  className = 'mt-5',
  desc = 'What your company asks of you after setup. Nothing here blocks you; answer whenever you like.',
}) => {
  const navigate = useNavigate();
  return (
    <Panel className={className}>
      <PanelHead kicker="Orbit" title="Questions for you" desc={desc} />
      <div className="oroom px-5 pb-5 md:px-6">
        <MyPart
          clientId={localStorage.getItem('client_id') || ''}
          onOpenDepartment={() => undefined}
          onAddDepartment={() => undefined}
          onInvite={() => undefined}
          onOpenTools={() => undefined}
          onOpenWork={(link) => navigate(link)}
        />
      </div>
    </Panel>
  );
};
